/**
 * Cartridge UI Extensions Hook
 *
 * Manages cartridge-provided UI extensions (menu items, dashboard widgets, entity tabs)
 * Uses Zustand for state management with React Query for data fetching
 */
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { apiRequest } from '../lib/api'

// ===========================================
// Types
// ===========================================

/**
 * Icon type - heroicon name string
 */
export type CartridgeIcon = string

/**
 * Menu item provided by a cartridge
 */
export interface CartridgeMenuItem {
  key: string
  labelKey: string
  href: string
  icon: CartridgeIcon
  adminOnly?: boolean
  position?: 'top' | 'middle' | 'bottom' | number
  group?: string
}

/**
 * Dashboard widget provided by a cartridge
 */
export interface CartridgeDashboardWidget {
  key: string
  titleKey: string
  size: 'small' | 'medium' | 'large' | 'full'
  priority?: number
  component: string
  props?: Record<string, unknown>
}

/**
 * Entity tab provided by a cartridge
 */
export interface CartridgeEntityTab {
  key: string
  labelKey: string
  icon: CartridgeIcon
  entities: ('asset' | 'account' | 'contact' | 'opportunity' | 'product' | 'quote' | 'order')[]
  component: string
  position?: number
  props?: Record<string, unknown>
}

/**
 * Extensions with cartridge metadata
 */
export interface CartridgeMenuItemWithSource {
  cartridgeSlug: string
  item: CartridgeMenuItem
}

export interface CartridgeDashboardWidgetWithSource {
  cartridgeSlug: string
  widget: CartridgeDashboardWidget
}

export interface CartridgeEntityTabWithSource {
  cartridgeSlug: string
  tab: CartridgeEntityTab
}

/**
 * API response for UI extensions
 */
interface UIExtensionsResponse {
  menuItems: CartridgeMenuItemWithSource[]
  dashboardWidgets: CartridgeDashboardWidgetWithSource[]
  entityTabs: CartridgeEntityTabWithSource[]
}

// ===========================================
// Store
// ===========================================

interface CartridgeUIState {
  /** All menu items from active cartridges */
  menuItems: CartridgeMenuItemWithSource[]
  /** All dashboard widgets from active cartridges */
  dashboardWidgets: CartridgeDashboardWidgetWithSource[]
  /** All entity tabs from active cartridges */
  entityTabs: CartridgeEntityTabWithSource[]
  /** Loading state */
  isLoading: boolean
  /** Error state */
  error: string | null
  /** Last fetch timestamp */
  lastFetched: number | null

  // Actions
  fetchExtensions: () => Promise<void>
  clearExtensions: () => void

  // Selectors (helpers)
  getMenuItemsForGroup: (group?: string) => CartridgeMenuItemWithSource[]
  getEntityTabsForType: (entityType: CartridgeEntityTab['entities'][number]) => CartridgeEntityTabWithSource[]
  getWidgetsByPriority: () => CartridgeDashboardWidgetWithSource[]
}

export const useCartridgeUI = create<CartridgeUIState>()(
  devtools(
    (set, get) => ({
      menuItems: [],
      dashboardWidgets: [],
      entityTabs: [],
      isLoading: false,
      error: null,
      lastFetched: null,

      fetchExtensions: async () => {
        // Skip if recently fetched (within 30 seconds)
        const { lastFetched, isLoading } = get()
        const now = Date.now()
        if (isLoading || (lastFetched && now - lastFetched < 30000)) {
          return
        }

        set({ isLoading: true, error: null })

        try {
          const response = await apiRequest<UIExtensionsResponse>('/cartridges/ui/extensions')

          set({
            menuItems: response.menuItems || [],
            dashboardWidgets: response.dashboardWidgets || [],
            entityTabs: response.entityTabs || [],
            isLoading: false,
            lastFetched: now,
          })
        } catch (error) {
          console.error('[CartridgeUI] Failed to fetch extensions:', error)
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load extensions',
          })
        }
      },

      clearExtensions: () => {
        set({
          menuItems: [],
          dashboardWidgets: [],
          entityTabs: [],
          lastFetched: null,
        })
      },

      getMenuItemsForGroup: (group?: string) => {
        const { menuItems } = get()
        return menuItems
          .filter(m => {
            if (group === undefined) return true
            return m.item.group === group
          })
          .sort((a, b) => {
            // Sort by position
            const posA = typeof a.item.position === 'number' ? a.item.position : 50
            const posB = typeof b.item.position === 'number' ? b.item.position : 50
            return posA - posB
          })
      },

      getEntityTabsForType: (entityType) => {
        const { entityTabs } = get()
        return entityTabs
          .filter(t => t.tab.entities.includes(entityType))
          .sort((a, b) => {
            const posA = a.tab.position ?? 100
            const posB = b.tab.position ?? 100
            return posA - posB
          })
      },

      getWidgetsByPriority: () => {
        const { dashboardWidgets } = get()
        return [...dashboardWidgets].sort((a, b) => {
          const prioA = a.widget.priority ?? 50
          const prioB = b.widget.priority ?? 50
          return prioA - prioB
        })
      },
    }),
    { name: 'CartridgeUIStore', enabled: process.env.NODE_ENV !== 'production' }
  )
)

// ===========================================
// Helper Hooks
// ===========================================

/**
 * Hook to get menu items for a specific group
 */
export function useCartridgeMenuItems(group?: string) {
  const menuItems = useCartridgeUI(state => state.menuItems)
  const isLoading = useCartridgeUI(state => state.isLoading)

  return {
    items: menuItems
      .filter(m => group === undefined || m.item.group === group)
      .sort((a, b) => {
        const posA = typeof a.item.position === 'number' ? a.item.position : 50
        const posB = typeof b.item.position === 'number' ? b.item.position : 50
        return posA - posB
      }),
    isLoading,
  }
}

/**
 * Hook to get entity tabs for a specific entity type
 */
export function useCartridgeEntityTabs(entityType: CartridgeEntityTab['entities'][number]) {
  const entityTabs = useCartridgeUI(state => state.entityTabs)
  const isLoading = useCartridgeUI(state => state.isLoading)

  return {
    tabs: entityTabs
      .filter(t => t.tab.entities.includes(entityType))
      .sort((a, b) => (a.tab.position ?? 100) - (b.tab.position ?? 100)),
    isLoading,
  }
}

/**
 * Hook to get dashboard widgets sorted by priority
 */
export function useCartridgeDashboardWidgets() {
  const dashboardWidgets = useCartridgeUI(state => state.dashboardWidgets)
  const isLoading = useCartridgeUI(state => state.isLoading)

  return {
    widgets: [...dashboardWidgets].sort(
      (a, b) => (a.widget.priority ?? 50) - (b.widget.priority ?? 50)
    ),
    isLoading,
  }
}
